import type { CardType } from './payment.types';

export interface PaymentConfigurationDisplayInput {
    readonly amount: number;
    readonly cardType: CardType | null;
    readonly allowSurcharge: boolean;
    readonly allowConvenienceFee: boolean;
    readonly surchargePercent: number | null;
    readonly convenienceFee: number | null;
}

export interface PaymentConfigurationDisplayValues {
    readonly surchargeAmount?: number;
    readonly convenienceFeeAmount?: number;
    readonly totalFeeAmount?: number;
    readonly totalAmountPaid?: number;
    readonly surchargePercentLabel?: string;
    readonly surchargeAmountLabel?: string;
    readonly convenienceFeeLabel?: string;
    readonly totalAmountLabel?: string;
}

function formatUsdAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function buildPaymentConfigurationDisplayValues(
    input: PaymentConfigurationDisplayInput
): PaymentConfigurationDisplayValues {
    if (!Number.isFinite(input.amount) || input.amount <= 0) {
        return {};
    }

    const convenienceFeeAmount: number = input.allowConvenienceFee
        ? (input.convenienceFee ?? 0)
        : 0;
    if (input.allowSurcharge && input.cardType === null) {
        return {
            convenienceFeeAmount,
            convenienceFeeLabel:
                input.allowConvenienceFee && input.convenienceFee !== null
                    ? formatUsdAmount(convenienceFeeAmount)
                    : undefined,
        };
    }

    const surchargeIsWaivedForDebitCard: boolean =
        input.allowSurcharge && input.cardType === 'DEBIT';
    const effectiveSurchargePercent: number =
        input.allowSurcharge && !surchargeIsWaivedForDebitCard ? (input.surchargePercent ?? 0) : 0;
    const surchargeAmount: number = Math.round(effectiveSurchargePercent * input.amount) / 100;
    const totalFeeAmount: number = surchargeAmount + convenienceFeeAmount;
    const totalAmountPaid: number = input.amount + totalFeeAmount;

    return {
        surchargeAmount,
        convenienceFeeAmount,
        totalFeeAmount,
        totalAmountPaid,
        surchargePercentLabel: input.allowSurcharge
            ? surchargeIsWaivedForDebitCard
                ? '—'
                : `${effectiveSurchargePercent.toFixed(2)}%`
            : undefined,
        surchargeAmountLabel: input.allowSurcharge
            ? surchargeIsWaivedForDebitCard
                ? '—'
                : formatUsdAmount(surchargeAmount)
            : undefined,
        convenienceFeeLabel:
            input.allowConvenienceFee && input.convenienceFee !== null
                ? formatUsdAmount(convenienceFeeAmount)
                : undefined,
        totalAmountLabel:
            input.allowSurcharge || input.allowConvenienceFee
                ? formatUsdAmount(totalAmountPaid)
                : undefined,
    };
}
